import { useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import type { ServiceItem } from '../data/site';
import { Icon } from './Icon';

type Props = {
  items: readonly ServiceItem[];
};

function getCards(track: HTMLDivElement): HTMLElement[] {
  return [...track.querySelectorAll<HTMLElement>('[data-service-card]')];
}

export function ServicesCarousel({ items }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollFrame = useRef(0);
  const [active, setActive] = useState(0);

  const updateActive = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const cards = getCards(track);
    if (!cards.length) return;

    const trackRect = track.getBoundingClientRect();
    const paddingLeft = Number.parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
    const targetLeft = trackRect.left + paddingLeft;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, cardIndex) => {
      const cardRect = card.getBoundingClientRect();
      const distance = Math.abs(cardRect.left - targetLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = cardIndex;
      }
    });

    setActive(nearestIndex);
  }, []);

  const updateEdgeSpacing = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const firstCard = getCards(track)[0];
    if (!firstCard) return;

    const computed = window.getComputedStyle(track);
    const paddingLeft = Number.parseFloat(computed.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(computed.paddingRight) || 0;
    const spacer = Math.max(0, track.clientWidth - firstCard.clientWidth - paddingLeft - paddingRight);
    track.style.setProperty('--services-edge-spacer', `${spacer}px`);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const scheduleUpdate = () => {
      if (scrollFrame.current) return;
      scrollFrame.current = window.requestAnimationFrame(() => {
        scrollFrame.current = 0;
        updateActive();
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      updateEdgeSpacing();
      window.requestAnimationFrame(updateActive);
    });

    resizeObserver.observe(track);
    track.addEventListener('scroll', scheduleUpdate, { passive: true });
    updateEdgeSpacing();
    window.requestAnimationFrame(updateActive);

    return () => {
      track.removeEventListener('scroll', scheduleUpdate);
      resizeObserver.disconnect();
      if (scrollFrame.current) window.cancelAnimationFrame(scrollFrame.current);
    };
  }, [items.length, updateActive, updateEdgeSpacing]);

  const goTo = useCallback((requestedIndex: number) => {
    const track = trackRef.current;
    if (!track) return;

    const cards = getCards(track);
    if (!cards.length) return;

    const nextIndex = Math.max(0, Math.min(cards.length - 1, requestedIndex));
    const trackRect = track.getBoundingClientRect();
    const cardRect = cards[nextIndex].getBoundingClientRect();
    const paddingLeft = Number.parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
    const targetLeft = track.scrollLeft + (cardRect.left - trackRect.left) - paddingLeft;

    setActive(nextIndex);
    track.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
  }, []);

  const canGoBack = active > 0;
  const canGoForward = active < items.length - 1;
  const trackStyle = { '--services-edge-spacer': '0px' } as CSSProperties;

  return (
    <div
      className="services-carousel"
      aria-roledescription="carrusel"
      aria-label="Otras especialidades"
      tabIndex={0}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft' && canGoBack) {
          event.preventDefault();
          goTo(active - 1);
        }
        if (event.key === 'ArrowRight' && canGoForward) {
          event.preventDefault();
          goTo(active + 1);
        }
      }}
    >
      <div ref={trackRef} className="services-carousel__track" style={trackStyle}>
        {items.map((item) => (
          <article data-service-card className="service-card" key={item.title}>
            <div className="service-card__image">
              <img src={item.image} alt="" loading="lazy" />
              <span aria-hidden="true" />
            </div>
            <div className="service-card__body">
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          </article>
        ))}
        <span className="services-carousel__edge" aria-hidden="true" />
      </div>

      {items.length > 1 ? (
        <div className="services-carousel__nav">
          <button
            type="button"
            className={!canGoBack ? 'is-disabled' : ''}
            disabled={!canGoBack}
            onClick={() => goTo(active - 1)}
            aria-label="Especialidad anterior"
          >
            <Icon name="arrowLeft" size={17} />
          </button>

          <div className="services-carousel__dots" aria-label="Seleccionar especialidad">
            {items.map((item, itemIndex) => (
              <button
                key={item.title}
                className={itemIndex === active ? 'is-active' : ''}
                type="button"
                aria-current={itemIndex === active ? 'true' : undefined}
                aria-label={`Ver especialidad ${itemIndex + 1}`}
                onClick={() => goTo(itemIndex)}
              />
            ))}
          </div>

          <button
            type="button"
            className={!canGoForward ? 'is-disabled' : ''}
            disabled={!canGoForward}
            onClick={() => goTo(active + 1)}
            aria-label="Especialidad siguiente"
          >
            <Icon name="arrowRight" size={17} />
          </button>
        </div>
      ) : null}
    </div>
  );
}
